// components/SelfPaced/AddOrEditSelfPacedCoursePage.js
// Restyled to match VC_FE_REPLIT_UX_DESIGNS with full theme support
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Row, Col, Spinner } from 'react-bootstrap';
import {
  Save, RotateCcw, BookOpen, Tag, Clock,
  IndianRupee, Image, Eye, EyeOff, Link2, Layers,
  Info, Sparkles, Languages, Shield, CalendarClock,
} from 'lucide-react';
import { BackButton, AppButton } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import { addSelfPacedCourse, getSelfPacedCourse, updateSelfPacedCourse, getCourseThumbnailUploadUrl } from '../../utils/selfPacedApi';
import { usePlan } from '../../contexts/PlanContext';
import './_self-paced-pages.scss';

const initialState = {
  name: '', shortCode: '', title: '', description: '', price: '',
  discountPrice: '', currency: 'INR', slug: '', category: '', level: '',
  language: 'English', duration: '', prerequisites: '', highlights: '',
  syllabus: '', isPublished: false, videoSecurity: false, thumbnail: null,
  expiryDays: '',
};

const generateSlug = (name) =>
  (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/* ── Section wrapper with icon ───────────────────────────── */
const Section = ({ icon, title, subtitle, children, className = '' }) => (
  <div className={`sp-form-section ${className}`}>
    <div className="sp-form-section-header">
      <div className="sp-form-section-icon">{icon}</div>
      <div>
        <h6 className="sp-form-section-title">{title}</h6>
        {subtitle && <p className="sp-form-section-subtitle">{subtitle}</p>}
      </div>
    </div>
    <div className="sp-form-section-body">{children}</div>
  </div>
);

/* ── Field wrapper ───────────────────────────────────────── */
const Field = ({ label, required, hint, children }) => (
  <div className="sp-field">
    <label className="sp-field-label">
      {label} {required && <span style={{ color: '#e11d48' }}>*</span>}
    </label>
    {children}
    {hint && <span className="sp-field-hint">{hint}</span>}
  </div>
);

const AddOrEditSelfPacedCoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(!!courseId);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [previewImg, setPreviewImg] = useState(null);
  const [slugManual, setSlugManual] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (courseId) {
      (async () => {
        try {
          const { data } = await getSelfPacedCourse(courseId);
          const c = data.course || data;
          setForm({
            ...initialState, name: c.name || '', shortCode: c.shortCode || '',
            title: c.title || '', description: c.description || '',
            price: c.price ?? '', discountPrice: c.discountPrice ?? '',
            currency: c.currency || 'INR', slug: c.slug || '',
            category: c.category || '', level: c.level || '',
            language: c.language || 'English', duration: c.duration || '',
            prerequisites: c.prerequisites || '', highlights: c.highlights || '',
            syllabus: c.syllabus || '', isPublished: c.isPublished || false,
            videoSecurity: c.videoSecurity || false,
            expiryDays: c.expiryDays ?? '',
            thumbnail: null,
          });
          if (c.thumbnailUrl || c.thumbnail) setPreviewImg(c.thumbnailUrl || c.thumbnail);
          if (c.slug) setSlugManual(true);
        } catch {
          setToast({ show: true, message: 'Failed to load course.', type: 'error' });
        } finally { setLoading(false); }
      })();
    }
    // eslint-disable-next-line
  }, [courseId]);

  const set = (name, value) => {
    setForm(p => ({ ...p, [name]: value }));
    if (name === 'name' && !slugManual) setForm(p => ({ ...p, slug: generateSlug(value) }));
  };

  const handleFile = (file) => {
    if (!file) return;
    setForm(p => ({ ...p, thumbnail: file }));
    setPreviewImg(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      const thumbnailFile = payload.thumbnail;
      delete payload.thumbnail;
      if (payload.price !== '') payload.price = Number(payload.price); else delete payload.price;
      if (payload.discountPrice !== '') payload.discountPrice = Number(payload.discountPrice); else delete payload.discountPrice;
      if (payload.expiryDays !== '') payload.expiryDays = Number(payload.expiryDays); else payload.expiryDays = null;
      ['shortCode', 'title', 'slug', 'category', 'level', 'duration', 'prerequisites', 'highlights', 'syllabus'].forEach(k => { if (!payload[k]) delete payload[k]; });

      let savedCourseId = courseId;
      if (courseId) {
        await updateSelfPacedCourse(courseId, payload);
      } else {
        const res = await addSelfPacedCourse(payload);
        savedCourseId = res.data?.course?._id || res.data?._id;
      }

      // Upload thumbnail via server-side upload if a new file was selected
      if (thumbnailFile && savedCourseId) {
        const formData = new FormData();
        formData.append('file', thumbnailFile);
        await getCourseThumbnailUploadUrl(savedCourseId, formData);
      }

      setToast({ show: true, message: courseId ? 'Course updated successfully!' : 'Course created successfully!', type: 'success' });
      setTimeout(() => navigate('/self-paced'), 1200);
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to save course.', type: 'error' });
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="sp-loading"><Spinner animation="border" variant="secondary" /></div>
  );

  const isEdit = !!courseId;

  return (
    <div className="sp-form-page">
      {/* Top Bar */}
      <div className="sp-form-topbar">
        <div className="sp-form-topbar__left">
          <BackButton onClick={() => navigate('/self-paced')} />
          <div className="sp-form-topbar__info">
            <h5>{isEdit ? 'Edit Course' : 'Create New Course'}</h5>
            <span>Self-Paced Learning</span>
          </div>
        </div>
        <div className="sp-form-topbar__actions">
          {/* Publish toggle pill */}
          <div
            className={`sp-publish-pill ${form.isPublished ? 'published' : 'draft'}`}
            onClick={() => set('isPublished', !form.isPublished)}
            role="button"
            tabIndex={0}
          >
            {form.isPublished ? <Eye size={14} /> : <EyeOff size={14} />}
            <span>{form.isPublished ? 'Published' : 'Draft'}</span>
          </div>
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => { setForm(initialState); setPreviewImg(null); setSlugManual(false); }}
          >
            <RotateCcw size={14} /> Reset
          </AppButton>
          <AppButton type="submit" form="courseForm" disabled={saving}>
            {saving ? (
              <><Spinner size="sm" animation="border" className="me-2" />Saving...</>
            ) : (
              <><Save size={15} /> {isEdit ? 'Update Course' : 'Save Course'}</>
            )}
          </AppButton>
        </div>
      </div>

      {/* Form Body */}
      <Form id="courseForm" onSubmit={handleSubmit}>
        <Row className="g-4 mt-0">
          {/* Left Column */}
          <Col lg={7} xl={8}>
            {/* Basic Details */}
            <Section icon={<BookOpen size={18} />} title="Basic Details" subtitle="The essential info students see first">
              <Row className="g-3">
                <Col md={7}>
                  <Field label="Course Name" required>
                    <Form.Control name="name" value={form.name}
                      onChange={e => set('name', e.target.value)} required
                      placeholder="e.g., React Masterclass 2026" />
                  </Field>
                </Col>
                <Col md={5}>
                  <Field label="Short Code" hint="Internal identifier">
                    <Form.Control className="sp-mono" name="shortCode" value={form.shortCode}
                      onChange={e => set('shortCode', e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                      placeholder="REACT_SP" maxLength={12} />
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Subtitle / Tagline">
                    <Form.Control name="title" value={form.title}
                      onChange={e => set('title', e.target.value)}
                      placeholder="A short, catchy line shown under the title" />
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Description" hint="Supports line breaks. Describe outcomes and target audience.">
                    <Form.Control as="textarea" rows={4} name="description" value={form.description}
                      onChange={e => set('description', e.target.value)}
                      placeholder="What will students learn? Who is this course for?" />
                  </Field>
                </Col>
              </Row>
            </Section>

            {/* Classification */}
            <Section icon={<Tag size={18} />} title="Classification" subtitle="Help students discover your course">
              <Row className="g-3">
                <Col md={4}>
                  <Field label="Category">
                    <Form.Control value={form.category}
                      onChange={e => set('category', e.target.value)}
                      placeholder="Web Development" />
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Level">
                    <Form.Select value={form.level} onChange={e => set('level', e.target.value)}>
                      <option value="">Select level</option>
                      {[['beginner', 'Beginner'], ['intermediate', 'Intermediate'], ['advanced', 'Advanced']].map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </Form.Select>
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Language">
                    <div className="sp-input-icon-wrap">
                      <Languages size={15} className="sp-input-icon" />
                      <Form.Control className="sp-input-with-icon" value={form.language}
                        onChange={e => set('language', e.target.value)} placeholder="English" />
                    </div>
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="URL Slug" hint="Auto-generated from name">
                    <div className="sp-input-icon-wrap">
                      <Link2 size={15} className="sp-input-icon" />
                      <Form.Control className="sp-input-with-icon sp-mono" value={form.slug}
                        onChange={e => { setSlugManual(true); set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); }}
                        placeholder="react-masterclass-2026" />
                    </div>
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Duration">
                    <div className="sp-input-icon-wrap">
                      <Clock size={15} className="sp-input-icon" />
                      <Form.Control className="sp-input-with-icon" value={form.duration}
                        onChange={e => set('duration', e.target.value)} placeholder="e.g., 20 hours" />
                    </div>
                  </Field>
                </Col>
              </Row>
            </Section>

            {/* Pricing */}
            <Section icon={<IndianRupee size={18} />} title="Pricing" subtitle="Set to 0 for free courses">
              <Row className="g-3">
                <Col md={4}>
                  <Field label="Price">
                    <div className="sp-input-icon-wrap">
                      <IndianRupee size={15} className="sp-input-icon" />
                      <Form.Control type="number" min={0} className="sp-input-with-icon" value={form.price}
                        onChange={e => set('price', e.target.value)} placeholder="0" />
                    </div>
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Discount Price">
                    <div className="sp-input-icon-wrap">
                      <Tag size={15} className="sp-input-icon" />
                      <Form.Control type="number" min={0} className="sp-input-with-icon" value={form.discountPrice}
                        onChange={e => set('discountPrice', e.target.value)} placeholder="Sale price" />
                    </div>
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Currency">
                    <Form.Select value={form.currency} onChange={e => set('currency', e.target.value)}>
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </Form.Select>
                  </Field>
                </Col>
              </Row>
              {form.price && form.discountPrice && Number(form.discountPrice) < Number(form.price) && (
                <div className="sp-discount-preview">
                  <Sparkles size={14} />
                  <span>{Math.round(((form.price - form.discountPrice) / form.price) * 100)}% discount applied</span>
                </div>
              )}
            </Section>

            {/* Extra Info */}
            <Section icon={<Layers size={18} />} title="Additional Details" subtitle="Enrich your course page" className="mb-4">
              <Row className="g-3">
                <Col xs={12}>
                  <Field label="Syllabus Outline">
                    <Form.Control as="textarea" rows={3} value={form.syllabus}
                      onChange={e => set('syllabus', e.target.value)}
                      placeholder="Main topics covered in this course" />
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Prerequisites">
                    <Form.Control as="textarea" rows={2} value={form.prerequisites}
                      onChange={e => set('prerequisites', e.target.value)}
                      placeholder="e.g., Basic JavaScript, HTML/CSS fundamentals" />
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Key Highlights" hint="Comma-separated selling points">
                    <Form.Control as="textarea" rows={2} value={form.highlights}
                      onChange={e => set('highlights', e.target.value)}
                      placeholder="Lifetime access, Certificate, Live Q&A sessions" />
                  </Field>
                </Col>
              </Row>
            </Section>
          </Col>

          {/* Right Column */}
          <Col lg={5} xl={4}>
            {/* Thumbnail dropzone */}
            <Section icon={<Image size={18} />} title="Course Thumbnail" subtitle="Recommended: 1280 × 720 px">
              <div
                className={`sp-dropzone ${dragActive ? 'active' : ''} ${previewImg ? 'has-image' : ''}`}
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={e => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files[0]); }}
              >
                {previewImg ? (
                  <img src={previewImg} alt="thumbnail" className="sp-dropzone-preview" />
                ) : (
                  <div className="sp-dropzone-placeholder">
                    <Image size={32} strokeWidth={1.5} />
                    <span>Drop image here or click to browse</span>
                    <small>JPG, PNG, WebP up to 5 MB</small>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" hidden
                  onChange={e => handleFile(e.target.files[0])} />
              </div>
              {previewImg && (
                <button type="button" className="sp-remove-img"
                  onClick={(e) => { e.stopPropagation(); setPreviewImg(null); setForm(p => ({ ...p, thumbnail: null })); }}>
                  Remove image
                </button>
              )}
            </Section>

            {/* Course Access Expiry */}
            <Section icon={<CalendarClock size={18} />} title="Course Access Expiry" subtitle="Auto-expire student access after a set number of days">
              <Field label="Expiry Days" hint="Leave empty or 0 for lifetime access. Only whole numbers allowed.">
                <Form.Control
                  type="number"
                  min={0}
                  step={1}
                  value={form.expiryDays}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    set('expiryDays', val);
                  }}
                  onKeyDown={e => { if (['e', 'E', '.', '-', '+'].includes(e.key)) e.preventDefault(); }}
                  placeholder="e.g., 90"
                />
              </Field>
              {form.expiryDays && Number(form.expiryDays) > 0 && (
                <div className="sp-discount-preview">
                  <CalendarClock size={14} />
                  <span>Students will have {form.expiryDays} day{Number(form.expiryDays) !== 1 ? 's' : ''} of access after enrollment</span>
                </div>
              )}
            </Section>

            {/* Video Security */}
            <VideoSecurityToggle form={form} set={set} />

            {/* Publish Card */}
            <Section icon={form.isPublished ? <Eye size={18} /> : <EyeOff size={18} />} title="Visibility">
              <div className="sp-visibility-card" onClick={() => set('isPublished', !form.isPublished)} role="button" tabIndex={0}>
                <div className={`sp-visibility-dot ${form.isPublished ? 'live' : ''}`} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                    {form.isPublished ? 'Published' : 'Draft'}
                  </div>
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {form.isPublished ? 'Visible in student catalog & purchasable' : 'Hidden — only admins can see this course'}
                  </small>
                </div>
                <Form.Check type="switch" checked={form.isPublished}
                  onChange={() => set('isPublished', !form.isPublished)}
                  className="ms-2" id="publish-switch" />
              </div>
            </Section>

            {/* Quick Tips */}
            <div className="sp-tips-card">
              <div className="sp-tips-header"><Info size={15} /> Quick Tips</div>
              <ul className="sp-tips-list">
                <li>Use a compelling thumbnail — it's the first thing students see</li>
                <li>Write a clear description that highlights learning outcomes</li>
                <li>Set a competitive price or offer it free to build your audience</li>
                <li>Add the course curriculum after saving</li>
              </ul>
            </div>
          </Col>
        </Row>
      </Form>

      <CommonToaster show={toast.show} message={toast.message} type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
};

/* \u2500\u2500 Video Security Toggle \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
const VideoSecurityToggle = ({ form, set }) => {
  const { hasPlanFeature } = usePlan();
  const planAllows = hasPlanFeature('secureVideoPlayer') && hasPlanFeature('videoSecurityEnabled');

  return (
    <Section icon={<Shield size={18} />} title="Video Security" subtitle="Require the desktop app for students to watch videos">
      <div
        className="sp-visibility-card"
        style={{ cursor: planAllows ? 'pointer' : 'default', opacity: planAllows ? 1 : 0.55 }}
        onClick={() => planAllows && set('videoSecurity', !form.videoSecurity)}
        role="button"
        tabIndex={0}
      >
        <div className={`sp-visibility-dot ${planAllows && form.videoSecurity ? 'live' : ''}`} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
            {planAllows && form.videoSecurity ? 'Enabled' : 'Disabled'}
          </div>
          <small style={{ color: 'hsl(var(--muted-foreground))' }}>
            {!planAllows
              ? (!hasPlanFeature('secureVideoPlayer')
                  ? 'Your plan does not include video security. Upgrade to enable.'
                  : 'Video security is not enabled in institute settings. Enable it in Settings \u2192 Modules.')
              : form.videoSecurity
                ? 'Students must use the Desktop App to watch videos in this course.'
                : 'Students can watch videos in the browser \u2014 no desktop app required.'}
          </small>
        </div>
        <Form.Check
          type="switch"
          id="videoSecurity"
          checked={planAllows ? !!form.videoSecurity : false}
          onChange={() => planAllows && set('videoSecurity', !form.videoSecurity)}
          disabled={!planAllows}
          className="ms-2"
        />
      </div>
      <small style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
        Download prevention and watermarks are always active regardless of this setting.
      </small>
    </Section>
  );
};

export default AddOrEditSelfPacedCoursePage;